import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { usePortfolio } from "@/context/PortfolioContext";
import { RevealTextInstant } from "@/components/site/RevealText";
import Reveal from "@/components/site/Reveal";
import Marquee from "@/components/site/Marquee";
import { SERVICES, INDUSTRIES, PROCESS_STEPS, CAD_STACK } from "@/lib/data";

const EASE = [0.76, 0, 0.24, 1];
const HERO_IMAGE = "https://images.unsplash.com/photo-1524514587686-e2909d726e9b?auto=format&fit=crop&w=1800&q=80";

function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const rotImg = useTransform(scrollYProgress, [0, 1], [0, -6]);

  return (
    <section ref={ref} className="relative pt-32 md:pt-40 pb-24 md:pb-40 overflow-hidden" data-testid="hero-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">

        {/* Top meta row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="grid grid-cols-12 gap-6 mb-10 md:mb-16"
        >
          <div className="col-span-6 md:col-span-3 az-tag">
            <span className="az-tag__dot" />
            <span>{t.home.heroKicker}</span>
          </div>
          <div className="col-span-6 md:col-span-3 md:col-start-10 md:text-right font-mono text-[10px] tracking-widest uppercase text-[#86868B]">
            IT / EN — {new Date().getFullYear()}
          </div>
        </motion.div>

        {/* Kinetic hero title */}
        <RevealTextInstant
          as="h1"
          lines={t.home.heroTitle}
          className="font-serif tracking-tighter leading-[0.92] text-[13vw] md:text-[8.5vw] lg:text-[7.5vw] xl:text-[7rem]"
          lineClassName="block"
        />

        {/* Bottom row: subtitle + CTAs + parallax image */}
        <div className="mt-14 md:mt-20 grid grid-cols-12 gap-6 md:gap-10 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
            className="col-span-12 md:col-span-5"
          >
            <p className="font-sans text-lg md:text-xl leading-relaxed text-[#0A0A0A] max-w-md">
              {t.home.heroSub}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/services" data-testid="hero-cta-services" className="az-btn">
                <span>{t.home.ctaServices}</span>
                <span className="az-btn__arrow" aria-hidden="true">→</span>
              </Link>
              <Link to="/contact" data-testid="hero-cta-contact" className="az-btn az-btn--ghost">
                <span>{t.home.ctaContact}</span>
                <span className="az-btn__arrow" aria-hidden="true">→</span>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.7 }}
            className="col-span-12 md:col-span-6 md:col-start-7"
          >
            <div className="relative aspect-[16/10] md:aspect-[16/11] overflow-hidden bg-[#0A0A0A]">
              <motion.img
                src={HERO_IMAGE}
                alt="Macro of precision mechanical gears"
                loading="eager"
                style={{ y: yImg, scale: scaleImg, rotate: rotImg }}
                className="w-full h-full object-cover will-change-transform"
              />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white font-mono text-[10px] tracking-widest uppercase">
                <span>Ref · 001 / Precision assembly</span>
                <span>Ø 88.00 mm</span>
              </div>
            </div>
            <div className="mt-4 flex items-baseline justify-between font-mono text-[10px] tracking-widest uppercase text-[#86868B]">
              <span>Studio · Antonio Zaccaria</span>
              <span>{t.common.role}</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  const { t } = useLang();
  return (
    <section className="relative py-24 md:py-40 bg-white border-t border-[#E5E5EA]" data-testid="manifesto-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3 az-tag">
            <span className="az-tag__dot" />
            <span>{t.home.statementKicker}</span>
          </div>
          <div className="col-span-12 md:col-span-9">
            <Reveal>
              <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.98] max-w-[16ch]">
                {t.home.statementTitle}
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="space-y-24 md:space-y-32">
          {t.home.chapters.map((ch, i) => (
            <div key={ch.n} className="grid grid-cols-12 gap-6 md:gap-10 items-start">
              <div className="col-span-3 md:col-span-3">
                <span className="font-mono text-[11px] tracking-widest text-[#86868B] uppercase block mb-3">{t.common.chapter}</span>
                <span className="font-mono text-6xl md:text-8xl text-[#0A0A0A] leading-none">{ch.n}</span>
              </div>
              <div className="col-span-9 md:col-span-8 md:col-start-5">
                <Reveal delay={0.05 * i}>
                  <h3 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight max-w-[22ch]">
                    {ch.title}
                  </h3>
                  <p className="mt-6 md:mt-8 font-sans text-base md:text-lg text-[#86868B] leading-relaxed max-w-[52ch]">
                    {ch.body}
                  </p>
                </Reveal>
              </div>
              <div className="col-span-12 mt-8 md:mt-4"><div className="az-rule" /></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Expertise() {
  const { t } = useLang();
  return (
    <section className="relative py-24 md:py-40 bg-[#F5F5F7] border-y border-[#E5E5EA]" data-testid="expertise-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="az-tag mb-6"><span className="az-tag__dot" /><span>{t.home.expertiseKicker}</span></div>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-6xl leading-[0.98] tracking-tighter">
                {t.home.expertiseTitle}
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <Reveal delay={0.05}>
              <p className="font-sans text-lg md:text-xl leading-relaxed text-[#86868B] max-w-[52ch]">
                {t.home.expertiseCopy}
              </p>
            </Reveal>

            <div className="mt-14 md:mt-20">
              <div className="font-mono text-[10px] tracking-widest uppercase text-[#0A0A0A] mb-6">{t.home.stackKicker}</div>
              <div className="flex flex-wrap gap-x-8 gap-y-4">
                {CAD_STACK.map((s) => (
                  <div key={s} className="font-serif text-2xl md:text-3xl tracking-tight text-[#0A0A0A]">
                    {s}<span className="text-[#0055FF]">.</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  const { t } = useLang();
  const preview = SERVICES.slice(0, 6);
  return (
    <section className="relative py-24 md:py-40 bg-white" data-testid="services-preview">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <div className="az-tag mb-6"><span className="az-tag__dot" /><span>{t.home.servicesKicker}</span></div>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-6xl leading-[0.98] tracking-tighter max-w-[18ch]">
                {t.home.servicesTitle}
              </h2>
            </Reveal>
          </div>
          <Link to="/services" data-testid="home-services-cta" className="az-link font-mono text-[11px] tracking-widest uppercase text-[#0A0A0A] self-start md:self-end">
            {t.home.servicesCta} →
          </Link>
        </div>

        <div className="border-t border-[#E5E5EA]">
          {preview.map((s, i) => {
            const item = t.services.items[s.id];
            return (
              <Reveal key={s.id} delay={i * 0.02}>
                <Link to="/services" className="group grid grid-cols-12 gap-6 items-center py-8 md:py-10 border-b border-[#E5E5EA] transition-colors duration-500 hover:bg-[#F5F5F7]">
                  <div className="col-span-1 font-mono text-[11px] tracking-widest text-[#86868B]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-8 md:col-span-6 font-serif text-2xl md:text-4xl tracking-tight">
                    {item.title}
                  </div>
                  <div className="hidden md:block col-span-4 font-sans text-sm text-[#86868B] max-w-[36ch]">
                    {item.problem}
                  </div>
                  <div className="col-span-3 md:col-span-1 text-right font-mono text-[11px] tracking-widest text-[#0A0A0A] transition-transform duration-500 group-hover:translate-x-1">
                    →
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function IndustriesStrip() {
  const { t } = useLang();
  return (
    <section className="relative py-24 md:py-40 bg-white border-t border-[#E5E5EA]" data-testid="industries-strip">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 mb-14">
          <div className="col-span-12 md:col-span-4">
            <div className="az-tag mb-6"><span className="az-tag__dot" /><span>{t.home.industriesKicker}</span></div>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tighter leading-[1.02]">{t.home.industriesTitle}</h2>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E5EA] border border-[#E5E5EA]">
          {INDUSTRIES.map((ind, i) => {
            const item = t.industries.items[ind.id];
            return (
              <Reveal key={ind.id} delay={i * 0.03} className="bg-white">
                <div className="p-8 md:p-10 min-h-[240px] flex flex-col justify-between transition-colors duration-500 hover:bg-[#F5F5F7]">
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#86868B]">
                    {String(i + 1).padStart(2, "0")} · {t.common.section}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl tracking-tight">{item.title}</h3>
                    <p className="mt-3 font-sans text-sm text-[#86868B] max-w-[38ch]">{item.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProcessPreview() {
  const { t } = useLang();
  return (
    <section className="relative py-24 md:py-40 bg-[#0A0A0A] text-white" data-testid="process-preview">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3 az-tag" style={{ color: "#FFFFFF" }}>
            <span className="az-tag__dot" />
            <span>{t.home.processKicker}</span>
          </div>
          <div className="col-span-12 md:col-span-9">
            <Reveal>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[0.98] max-w-[22ch]">
                {t.home.processTitle}
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="space-y-6 md:space-y-8">
          {PROCESS_STEPS.map((id, i) => {
            const step = t.process.steps[id];
            return (
              <Reveal key={id} delay={i * 0.04}>
                <div className="grid grid-cols-12 gap-6 items-baseline border-t border-white/15 pt-6 md:pt-8">
                  <div className="col-span-2 md:col-span-1 font-mono text-sm md:text-base tracking-widest text-[#0055FF]">
                    0{i + 1}
                  </div>
                  <div className="col-span-10 md:col-span-5 font-serif text-2xl md:text-4xl tracking-tight">
                    {step.title}
                  </div>
                  <div className="col-span-12 md:col-span-6 font-sans text-sm md:text-base text-white/60 leading-relaxed max-w-[52ch]">
                    {step.body}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-16">
          <Link to="/process" data-testid="home-process-cta" className="az-link font-mono text-[11px] tracking-widest uppercase text-white">
            {t.common.readMore} →
          </Link>
        </div>
      </div>
    </section>
  );
}

function SelectedProjects() {
  const { t, lang } = useLang();
  const { visible, items, imageUrl } = usePortfolio();
  if (!visible || items.length === 0) return null;
  const preview = items.slice(0, 4);
  const layouts = ["col-span-12 md:col-span-7", "col-span-12 md:col-span-5", "col-span-12 md:col-span-5", "col-span-12 md:col-span-7"];
  const aspects = ["aspect-[16/10]", "aspect-[16/12]", "aspect-[16/10]", "aspect-[16/12]"];
  return (
    <section className="relative py-24 md:py-40 bg-white" data-testid="selected-projects">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 mb-14">
          <div className="col-span-12 md:col-span-4">
            <div className="az-tag mb-6"><span className="az-tag__dot" /><span>{t.home.selectedKicker}</span></div>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[0.98]">
                {t.home.selectedTitle}
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 md:text-right self-end">
            <Link to="/portfolio" className="az-link font-mono text-[11px] tracking-widest uppercase" data-testid="home-portfolio-cta">
              {t.nav.portfolio} →
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {preview.map((p, i) => {
            const title = (lang === "en" ? p.title_en : p.title_it) || p.title_it || p.title_en;
            return (
              <Reveal key={p.id} delay={i * 0.05} className={layouts[i]}>
                <Link to="/portfolio" data-cursor-label={t.common.viewProject} className="block az-card">
                  <div className={`relative ${aspects[i]}`}>
                    {p.image_file_id ? (
                      <img src={imageUrl(p.id)} alt={title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="absolute inset-0 bg-[#0A0A0A]" />
                    )}
                    <div className="absolute top-4 left-4 font-mono text-[10px] tracking-widest uppercase text-white/80">
                      {String(i + 1).padStart(2, "0")} / {t.portfolio.categories[p.category] || p.category}
                    </div>
                    <div className="az-card__meta">
                      <div className="font-serif text-2xl md:text-4xl tracking-tight max-w-[70%]">{title}</div>
                      <div className="font-mono text-[10px] tracking-widest uppercase text-white/70">{p.coord || ""}</div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ContactBlock() {
  const { t } = useLang();
  return (
    <section className="relative py-32 md:py-48 bg-[#F5F5F7] border-t border-[#E5E5EA]" data-testid="home-contact">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-3 az-tag mb-6">
            <span className="az-tag__dot" />
            <span>{t.home.contactKicker}</span>
          </div>
          <div className="col-span-12 md:col-span-9">
            <Reveal>
              <h2 className="font-serif text-5xl md:text-8xl tracking-tighter leading-[0.9] max-w-[18ch]">
                {t.home.contactTitle}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 font-sans text-lg md:text-xl text-[#86868B] leading-relaxed max-w-[46ch]">
                {t.home.contactCopy}
              </p>
              <div className="mt-10 flex flex-wrap gap-4 items-center">
                <Link to="/contact" data-testid="home-contact-cta" className="az-btn">
                  <span>{t.nav.cta}</span>
                  <span className="az-btn__arrow" aria-hidden="true">→</span>
                </Link>
                <a href={`mailto:${t.common.email}`} className="az-link font-mono text-[11px] tracking-widest uppercase">
                  {t.common.email}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { t } = useLang();
  return (
    <div data-testid="page-home">
      <Hero />
      <Marquee items={t.marquee} tag="AZ · Marquee" />
      <Manifesto />
      <Expertise />
      <ServicesPreview />
      <IndustriesStrip />
      <ProcessPreview />
      <SelectedProjects />
      <ContactBlock />
    </div>
  );
}
